import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';

// NOVO: Importando o store
import { useAuthStore } from '../store/authStore';
import { useLoadingState } from '../hooks/useLoadingState';
import LoadingButton from './LoadingButton';
import { convertBrazilianDateToISO, convertISOToBrazilianDate } from '../utils/dateUtils';
import { getMembershipType, getMembershipTypeBadge } from '../utils/memberUtils';

// ALTERADO: O componente não recebe mais `isAdmin`
const MemberForm = ({ onClose, onSave, connects, editingMember, leaderConnects, onCheckDuplicate, onOpenLeadershipTrack }) => {
    // NOVO: Buscando o status de admin diretamente do store
    const { isAdmin } = useAuthStore();
    const { isLoading, setLoading, setIdle } = useLoadingState();

    const [formData, setFormData] = useState({
        name: '',
        knownBy: '',
        dob: '',
        email: '',
        street: '',
        neighborhood: '',
        city: '',
        zipCode: '',
        phone: '',
        gender: '',
        connectId: '',
        isAdmin: false,
        milestones: {
            salvation: { date: '' },
            initialDiscipleship: { date: '' },
            baptism: { date: '' },
            membership: { date: '' },
            connectTraining: { date: '' },
        }
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    
    useEffect(() => {
        if (editingMember) {
            const milestones = editingMember.milestones || {
                salvation: { date: '' },
                initialDiscipleship: { date: '' },
                baptism: { date: '' },
                membership: { date: '' },
                connectTraining: { date: '' },
            };
            setFormData({
                name: editingMember.name || '',
                knownBy: editingMember.knownBy || '',
                dob: editingMember.dob ? convertISOToBrazilianDate(editingMember.dob) : '',
                email: editingMember.email || '',
                street: editingMember.street || editingMember.address || '',
                neighborhood: editingMember.neighborhood || '',
                city: editingMember.city || '',
                zipCode: editingMember.zipCode || '',
                phone: editingMember.phone || '',
                gender: editingMember.gender || '',
                connectId: editingMember.connectId || '',
                isAdmin: editingMember.isAdmin || false,
                milestones: {
                    salvation: { date: milestones.salvation?.date ? convertISOToBrazilianDate(milestones.salvation.date) : '' },
                    initialDiscipleship: { date: milestones.initialDiscipleship?.date ? convertISOToBrazilianDate(milestones.initialDiscipleship.date) : '' },
                    baptism: { date: milestones.baptism?.date ? convertISOToBrazilianDate(milestones.baptism.date) : '' },
                    membership: { date: milestones.membership?.date ? convertISOToBrazilianDate(milestones.membership.date) : '' },
                    connectTraining: { date: milestones.connectTraining?.date ? convertISOToBrazilianDate(milestones.connectTraining.date) : '' },
                }
            });
        } else {
            setFormData({
                name: '',
                knownBy: '',
                dob: '',
                email: '',
                street: '',
                neighborhood: '',
                city: '',
                zipCode: '',
                phone: '',
                gender: '',
                connectId: '',
                isAdmin: false,
                milestones: {
                    salvation: { date: '' },
                    initialDiscipleship: { date: '' },
                    baptism: { date: '' },
                    membership: { date: '' },
                    connectTraining: { date: '' },
                }
            });
        }
    }, [editingMember]);

    const validateField = (name, value) => {
        const errors = {};
        
        switch (name) {
            case 'email':
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    errors.email = 'E-mail inválido';
                }
                break;
            case 'phone':
                if (value && !/^\(?\d{2}\)?[\s-]?\d{4,5}[\s-]?\d{4}$/.test(value.replace(/\D/g, ''))) {
                    errors.phone = 'Telefone deve ter 10 ou 11 dígitos';
                }
                break;
            case 'name':
                if (value && value.trim().length < 2) {
                    errors.name = 'Nome deve ter pelo menos 2 caracteres';
                }
                break;
            case 'street':
                // Endereço não é mais obrigatório
                break;
            case 'neighborhood':
                // Bairro não é mais obrigatório
                break;
            case 'city':
                // Município não é mais obrigatório
                break;
            case 'zipCode':
                // CEP não é mais obrigatório, mas se preenchido deve ter formato válido
                if (value && value.trim() !== '' && !/^\d{5}-?\d{3}$/.test(value.replace(/\D/g, ''))) {
                    errors.zipCode = 'CEP deve ter 8 dígitos';
                }
                break;
            case 'dob':
                if (value) {
                    const birthDate = new Date(value);
                    const today = new Date();
                    const age = today.getFullYear() - birthDate.getFullYear();
                    if (age < 0 || age > 120) {
                        errors.dob = 'Data de nascimento inválida';
                    }
                }
                break;
            case 'connectId':
                // Connect é obrigatório para não-admins que têm connects disponíveis
                if (!isAdmin && (!value || value.trim() === '') && leaderConnects && leaderConnects.length > 0) {
                    errors.connectId = 'Connect é obrigatório';
                }
                break;
            default:
                break;
        }
        
        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'dob') {
            // Para data de nascimento, salva o valor como está sendo digitado
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Validação em tempo real
        const fieldError = validateField(name, value);
        setFieldErrors(prev => ({ ...prev, ...fieldError, [name]: fieldError[name] || null }));
    };
    
    const handleDateChange = (fieldName, value) => {
        // Remove caracteres não numéricos
        const cleanValue = value.replace(/\D/g, '');
        
        // Aplica máscara dd/mm/aaaa
        let formattedValue = cleanValue;
        if (cleanValue.length >= 2) {
            formattedValue = cleanValue.slice(0, 2) + '/' + cleanValue.slice(2);
        }
        if (cleanValue.length >= 4) {
            formattedValue = cleanValue.slice(0, 2) + '/' + cleanValue.slice(2, 4) + '/' + cleanValue.slice(4, 8);
        }
        
        setFormData(prev => ({ ...prev, [fieldName]: formattedValue }));
        
        // Validação em tempo real
        const fieldError = validateField(fieldName, formattedValue);
        setFieldErrors(prev => ({ ...prev, ...fieldError, [fieldName]: fieldError[fieldName] || null }));
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            // Validação completa de todos os campos
            const allErrors = {};
            Object.keys(formData).forEach(key => {
                if (key !== 'milestones') {
                    const fieldError = validateField(key, formData[key]);
                    Object.assign(allErrors, fieldError);
                }
            });
            
            // Verificação de campos obrigatórios
            if (!formData.name?.trim()) allErrors.name = 'Nome é obrigatório';
            if (!formData.dob) allErrors.dob = 'Data de nascimento é obrigatória';
            if (!formData.phone?.trim()) allErrors.phone = 'Telefone é obrigatório';
            if (!formData.gender) allErrors.gender = 'Sexo é obrigatório';
            // E-mail não é mais obrigatório
            
            // Validação do Connect para não-admins
            if (!isAdmin && (!formData.connectId || formData.connectId.trim() === '') && leaderConnects && leaderConnects.length > 0) {
                allErrors.connectId = 'Connect é obrigatório';
            }
            
            setFieldErrors(allErrors);
            
            if (Object.keys(allErrors).length > 0) {
                setError('Por favor, corrija os erros nos campos destacados.');
                setIdle(); // Restaura o botão de salvar
                return;
            }

            const finalData = { ...formData };
            
            // Converte data de nascimento para ISO
            if (finalData.dob) {
                finalData.dob = convertBrazilianDateToISO(finalData.dob);
            }
            
            // Se 'Conhecido por' não foi preenchido, usar o primeiro nome
            if (!finalData.knownBy?.trim() && finalData.name?.trim()) {
                finalData.knownBy = finalData.name.split(' ')[0];
            }
            
            // Preservar os marcos do membro intactos (gerenciados exclusivamente pelo Trilho de Liderança)
            if (editingMember?.milestones) {
                finalData.milestones = editingMember.milestones;
            } else {
                delete finalData.milestones;
            }

            if (!editingMember) {
                await onCheckDuplicate(finalData);
                // Reset loading state após verificação de duplicata
                setIdle();
            } else {
                onSave(finalData, editingMember);
                setIdle();
            }
        } catch (error) {
            setError('Erro ao salvar membro. Tente novamente.');
            setIdle();
        }
    };

    const availableConnects = isAdmin ? connects : leaderConnects;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && <p className="text-red-600 bg-red-100 p-2 rounded-md">{error}</p>}

            {editingMember && (() => {
                const type = getMembershipType(editingMember);
                const badge = getMembershipTypeBadge(type);
                return (
                    <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Classificação:</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${badge.bg} ${badge.color} ${badge.border}`}>
                                {badge.label}
                            </span>
                            <span className="text-xs text-gray-700 font-medium">({badge.name})</span>
                        </div>
                        <span className="text-xs text-gray-500">
                            {type === 'MI' ? 'Batismo e Aliança de Membresia no Trilho' : 'Membro cadastrado no Connect'}
                        </span>
                    </div>
                );
            })()}
            
            <fieldset className="border p-4 rounded-md">
                <legend className="px-2 font-semibold">Dados Pessoais</legend>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                        <input 
                            type="text" 
                            name="name" 
                            id="name" 
                            value={formData.name} 
                            onChange={handleChange} 
                            className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                fieldErrors.name 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-[#DC2626]'
                            }`} 
                            placeholder="Nome completo" 
                        />
                        {fieldErrors.name && <p className="text-red-600 text-sm mt-1">{fieldErrors.name}</p>}
                    </div>
                    <div>
                        <label htmlFor="knownBy" className="block text-sm font-medium text-gray-700 mb-1">Conhecido por</label>
                        <input 
                            type="text" 
                            name="knownBy" 
                            id="knownBy" 
                            value={formData.knownBy} 
                            onChange={handleChange} 
                            className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                fieldErrors.knownBy 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-[#DC2626]'
                            }`} 
                            placeholder="Como a pessoa é conhecida (deixe vazio para usar o primeiro nome)" 
                        />
                        {fieldErrors.knownBy && <p className="text-red-600 text-sm mt-1">{fieldErrors.knownBy}</p>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                            <input 
                                type="text" 
                                name="dob" 
                                id="dob" 
                                value={formData.dob || ''} 
                                onChange={(e) => handleDateChange('dob', e.target.value)} 
                                placeholder="dd/mm/aaaa"
                                maxLength="10"
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.dob 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                            />
                            {fieldErrors.dob && <p className="text-red-600 text-sm mt-1">{fieldErrors.dob}</p>}
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                            <input 
                                type="email" 
                                name="email" 
                                id="email" 
                                value={formData.email} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.email 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="email@exemplo.com" 
                            />
                            {fieldErrors.email && <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                            <input 
                                type="text" 
                                name="street" 
                                id="street" 
                                value={formData.street} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.street 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="Rua e número" 
                            />
                            {fieldErrors.street && <p className="text-red-600 text-sm mt-1">{fieldErrors.street}</p>}
                        </div>
                        <div>
                            <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                            <input 
                                type="text" 
                                name="neighborhood" 
                                id="neighborhood" 
                                value={formData.neighborhood} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.neighborhood 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="Bairro" 
                            />
                            {fieldErrors.neighborhood && <p className="text-red-600 text-sm mt-1">{fieldErrors.neighborhood}</p>}
                        </div>
                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">Município</label>
                            <input 
                                type="text" 
                                name="city" 
                                id="city" 
                                value={formData.city} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.city 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="Município" 
                            />
                            {fieldErrors.city && <p className="text-red-600 text-sm mt-1">{fieldErrors.city}</p>}
                        </div>
                        <div>
                            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                            <input 
                                type="text" 
                                name="zipCode" 
                                id="zipCode" 
                                value={formData.zipCode} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.zipCode 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="00000-000" 
                            />
                            {fieldErrors.zipCode && <p className="text-red-600 text-sm mt-1">{fieldErrors.zipCode}</p>}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Celular</label>
                            <input 
                                type="tel" 
                                name="phone" 
                                id="phone" 
                                value={formData.phone} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.phone 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`} 
                                placeholder="(99) 99999-9999" 
                            />
                            {fieldErrors.phone && <p className="text-red-600 text-sm mt-1">{fieldErrors.phone}</p>}
                        </div>
                        <div>
                            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
                            <select 
                                name="gender" 
                                id="gender" 
                                value={formData.gender} 
                                onChange={handleChange} 
                                className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                    fieldErrors.gender 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:ring-[#DC2626]'
                                }`}
                            >
                                <option value="">Selecione...</option>
                                <option value="Masculino">Masculino</option>
                                <option value="Feminino">Feminino</option>
                            </select>
                            {fieldErrors.gender && <p className="text-red-600 text-sm mt-1">{fieldErrors.gender}</p>}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="connectId" className="block text-sm font-medium text-gray-700 mb-1">
                            Connect {!isAdmin && availableConnects.length > 0 && <span className="text-red-500">*</span>}
                        </label>
                        <select
                            name="connectId"
                            id="connectId"
                            value={formData.connectId}
                            onChange={handleChange}
                            className={`w-full bg-gray-100 text-gray-900 rounded-md p-2 border focus:ring-2 ${
                                fieldErrors.connectId 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:ring-[#DC2626]'
                            }`}
                        >
                            <option value="">
                                {isAdmin ? 'Selecione um Connect (opcional)' : 'Selecione um Connect'}
                            </option>
                            {availableConnects.map(connect => (
                                <option key={connect.id} value={connect.id}>
                                    {connect.number} - {connect.name}
                                </option>
                            ))}
                        </select>
                        {fieldErrors.connectId && <p className="text-red-600 text-sm mt-1">{fieldErrors.connectId}</p>}
                        <p className="text-xs text-gray-500 mt-1">
                            {isAdmin 
                                ? 'Administradores podem deixar vazio ou escolher qualquer Connect'
                                : 'Obrigatório: escolha um dos Connects que você lidera'
                            }
                        </p>
                    </div>
                    
                    {/* Campo isAdmin - visível apenas para administradores */}
                    {isAdmin && (
                        <div>
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="isAdmin"
                                    checked={formData.isAdmin}
                                    onChange={(e) => setFormData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-gray-700">Administrador do Sistema</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                                Concede acesso total ao sistema (apenas administradores podem alterar)
                            </p>
                        </div>
                    )}
                </div>
            </fieldset>

            <div className={`flex items-center pt-4 ${editingMember ? 'justify-between' : 'justify-end'}`}>
                {editingMember && (
                    <button 
                        type="button"
                        onClick={() => onOpenLeadershipTrack && onOpenLeadershipTrack(editingMember)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
                        title="Abrir Trilho de Liderança"
                    >
                        <TrendingUp size={16} />
                        Trilho de Liderança
                    </button>
                )}
                
                <div className="flex space-x-3">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 transition-all" disabled={isLoading}>Cancelar</button>
                    <LoadingButton
                        type="submit"
                        isLoading={isLoading}
                        className="px-4 py-2 rounded-md bg-[#DC2626] text-white font-semibold hover:bg-[#991B1B] transition-all"
                    >
                        {editingMember ? 'Salvar Alterações' : 'Adicionar Membro'}
                    </LoadingButton>
                </div>
            </div>
        </form>
    );
};

export default MemberForm;
